/**
 * EmptyState — shown when the company has no filings/buildings yet.
 *
 * Two CTAs:
 *   - Primary: "Add a building" (opens NewBuildingDialog via parent)
 *   - Secondary: "Connect Portfolio Manager" — opens the same dialog; the
 *     NewBuildingDialog already exposes a "From Portfolio Manager" tab, so
 *     both entry points land in the right place. Copy makes the choice
 *     clear so an owner with an existing ESPM portfolio doesn't start
 *     entering a single building by hand.
 *
 * Intentionally uses `density="comfortable"` and centered content — this is
 * the one place on the dashboard where marketing-hero density is right.
 */

import React from "react";
import { Building2, Link2 } from "lucide-react";

import { Button } from "@/components/shared/ui/button";
import { Card } from "@/components/shared/ui/card";

interface EmptyStateProps {
  onAddBuilding: () => void;
  onConnectEspm: () => void;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  onAddBuilding,
  onConnectEspm,
}) => (
  <Card density="comfortable" className="items-center text-center">
    <div
      className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-muted"
      aria-hidden="true"
    >
      <Building2 className="h-6 w-6 text-muted-foreground" />
    </div>
    <div className="space-y-2">
      <h2 className="text-xl font-semibold text-foreground">
        No buildings yet
      </h2>
      <p className="max-w-md text-sm text-muted-foreground">
        Add your first building to generate an LL97 or LL88 filing draft. If
        your portfolio already lives in Energy Star Portfolio Manager, connect
        it to import in bulk.
      </p>
    </div>
    <div className="flex flex-col gap-3 sm:flex-row">
      <Button variant="default" onClick={onAddBuilding}>
        <Building2 className="mr-2 h-4 w-4" aria-hidden="true" />
        Add a building
      </Button>
      <Button variant="outline" onClick={onConnectEspm}>
        <Link2 className="mr-2 h-4 w-4" aria-hidden="true" />
        Connect Portfolio Manager
      </Button>
    </div>
  </Card>
);

export default EmptyState;
